import { MapPin, Calendar, DollarSign, MessageSquare, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface AcceptedLead {
  id: string;
  name: string;
  project: string;
  location: string;
  budget: string;
  acceptedDate: string;
  nextStep: string;
  status: 'contacted' | 'meeting-scheduled' | 'proposal-sent';
}

interface AcceptedLeadsProps {
  leads?: AcceptedLead[];
}

const defaultLeads: AcceptedLead[] = [
  {
    id: '1',
    name: 'Robert Taylor',
    project: 'Home Addition',
    location: 'Cedar Park, TX',
    budget: '$85,000',
    acceptedDate: 'Jan 7, 2026',
    nextStep: 'Site visit scheduled',
    status: 'meeting-scheduled'
  },
  {
    id: '2',
    name: 'Emily Rodriguez',
    project: 'Master Bath Renovation',
    location: 'Pflugerville, TX',
    budget: '$32,000',
    acceptedDate: 'Jan 6, 2026',
    nextStep: 'Send detailed proposal',
    status: 'contacted'
  },
  {
    id: '3',
    name: 'Michael Brown',
    project: 'Outdoor Living Space',
    location: 'Leander, TX',
    budget: '$45,000',
    acceptedDate: 'Jan 5, 2026',
    nextStep: 'Awaiting client response',
    status: 'proposal-sent'
  },
  {
    id: '4',
    name: 'Jennifer Lee',
    project: 'Basement Finishing',
    location: 'Austin, TX',
    budget: '$55,000',
    acceptedDate: 'Jan 4, 2026',
    nextStep: 'Schedule follow-up call',
    status: 'contacted'
  }
];

const statusConfig = {
  'contacted': {
    label: 'Contacted',
    color: 'bg-blue-50 text-blue-700 border-blue-200'
  },
  'meeting-scheduled': {
    label: 'Meeting Scheduled',
    color: 'bg-green-50 text-green-700 border-green-200'
  },
  'proposal-sent': {
    label: 'Proposal Sent',
    color: 'bg-purple-50 text-purple-700 border-purple-200'
  }
};

export function AcceptedLeads({ leads = defaultLeads }: AcceptedLeadsProps) {
const navigate = useNavigate()
  return (
    <div className="dashboard-panel leads-panel bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-[500] text-gray-900 tracking-tight">Accepted Leads</h3>
          <p className="text-sm text-gray-600 mt-1">{leads.length} leads in progress</p>
        </div>
        <button onClick={()=>{
navigate("/project-ongoing-leads")
        }} className="text-sm bg-transparent  font-[500] border-0 text-indigo-600 hover:text-indigo-700 transition-colors">
          View All
        </button>
      </div>

      <div className="space-y-3">
        {leads.map((lead) => (
          <div 
            key={lead.id}
            className="border border-gray-200 rounded-lg p-3 hover:border-indigo-800 border-solid hover:bg-indigo-50/20 transition-all duration-200 group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-[500] text-[17px] text-gray-900">{lead.name}</div>
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div className="text-sm text-gray-600 font-medium">{lead.project}</div>
              </div>
              {/* <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${statusConfig[lead.status]?.color}`}>
                {statusConfig[lead.status]?.label}
              </span> */}
            </div>

            <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                <span>{lead.location}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                <span>{lead.budget}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                <span>{lead.acceptedDate}</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-solid border-l-0 border-r-0 border-b-0 border-gray-100">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Next:</span>
                <span className="text-gray-700 font-medium">{lead.nextStep}</span>
              </div>
              <button className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity">
                <MessageSquare className="w-4 h-4" />
                Message
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
